/**
 * Seedance 素材上传：签名 URL → PUT R2 → confirmUpload 建 UserMaterial 记录。
 * 与 ai-video 页面同款 pattern，抽出复用。
 */
import axios from 'axios';
import api from '~/api';
import { assetSizeLimit, bytesToMB } from '~/utils/seedanceMedia';

const MIME_MAP: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  mp4: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  m4a: 'audio/mp4',
  ogg: 'audio/ogg',
  aac: 'audio/aac',
};

function getMimeType(name: string) {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  return MIME_MAP[ext] ?? 'application/octet-stream';
}

function detectFormat(file: File): 'photo' | 'video' | 'gif' | 'audio' {
  const type = file.type || getMimeType(file.name);
  if (type.includes('gif')) return 'gif';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  return 'photo';
}

export function useSeedanceUpload() {
  const uploading = ref(false);
  const progress = ref(0);

  const upload = async (file: File) => {
    // 硬性大小闸（最终保证）：视频 ≤50MB，图片/音频 ≤20MB。
    // 正常调用方（AssetPickerDialog）已提前拦截并给本地化提示、把超大视频/音频引导去裁剪；
    // 这里兜底，确保任何路径都不会把超限文件传上去。
    const fmt = detectFormat(file);
    if (file.size > assetSizeLimit(fmt)) {
      const cap = fmt === 'video' ? 50 : 20;
      throw new Error(`File exceeds ${cap}MB limit (this file is ${bytesToMB(file.size)}MB)`);
    }
    uploading.value = true;
    progress.value = 0;
    try {
      const ext = (file.name.split('.').pop() || 'bin').toLowerCase();
      const sign = await api.getSignUrl(ext);
      progress.value = 20;
      await axios.put(sign.sign_url, file, {
        headers: { 'Content-Type': file.type || getMimeType(file.name) },
      });
      progress.value = 70;
      const url = (sign as any).file_url || `https://cdn.treekee.com/bestfaceswap/uploads/${sign.material_id}.${ext}`;
      const format = detectFormat(file);
      // 所有类型（含 audio）都 confirmUpload 入库，Library / 素材选择弹窗才能查到
      await api.confirmUpload({
        material_id: sign.material_id,
        url,
        format,
        size: file.size,
      });
      progress.value = 100;
      return { url, materialId: sign.material_id, format };
    } finally {
      uploading.value = false;
    }
  };

  return { upload, uploading, progress };
}
